import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { DriverSelect, toDriverOption } from '@/components/common/DriverSelect';

interface Avail {
  id: string; code: string;
  plate_en?: string; full_name_en?: string;
  availability: 'free' | 'on_trip' | 'maintenance' | 'inactive';
  current_trip_code: string | null;
}

interface ReassignSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  currentVehicleId: string | null;
  currentDriverId: string | null;
  onReassigned: () => void;
}

/**
 * PR-F4 MAY 11 FLATBED OPERATOR — admin reassigns vehicle/driver on a trip
 * (pre-loaded states by default; force=true after loaded with reason).
 */
export function ReassignSheet({ open, onOpenChange, tripId, currentVehicleId, currentDriverId, onReassigned }: ReassignSheetProps) {
  const [vehicles, setVehicles] = useState<Avail[]>([]);
  const [drivers, setDrivers] = useState<Avail[]>([]);
  const [vehicleId, setVehicleId] = useState<string>(currentVehicleId ?? '');
  const [driverId, setDriverId] = useState<string>(currentDriverId ?? '');
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState<Array<{ kind: string; currentTrip: string }>>([]);

  useEffect(() => {
    if (!open) return;
    setVehicleId(currentVehicleId ?? '');
    setDriverId(currentDriverId ?? '');
    setReason('');
    setForce(false);
    setConflicts([]);
    apiClient.get<{ vehicles: Avail[]; drivers: Avail[] }>('/api/v1/flatbed/availability?vehicleType=flatbed')
      .then((r) => { setVehicles(r.data.vehicles); setDrivers(r.data.drivers); })
      .catch(() => null);
  }, [open, currentVehicleId, currentDriverId]);

  function tone(avail: string) {
    if (avail === 'free')        return 'text-emerald-700 dark:text-emerald-400';
    if (avail === 'on_trip')     return 'text-amber-700 dark:text-amber-400';
    return 'text-rose-700 dark:text-rose-400';
  }

  async function submit() {
    if (!reason || reason.trim().length < 2) { window.alert('Reason required'); return; }
    const body: Record<string, unknown> = { reason, force };
    if (vehicleId && vehicleId !== currentVehicleId) body.vehicleId = vehicleId;
    if (driverId && driverId !== currentDriverId)   body.driverId  = driverId;
    if (!body.vehicleId && !body.driverId) { window.alert('Pick a different vehicle or driver'); return; }
    setSubmitting(true);
    try {
      const r = await apiClient.patch<{ ok: boolean; conflicts?: Array<{ kind: string; currentTrip: string }> }>(`/api/v1/flatbed/trips/${tripId}/reassign`, body);
      if (r.data.conflicts && r.data.conflicts.length > 0 && !force) {
        setConflicts(r.data.conflicts);
        return;
      }
      onReassigned();
      onOpenChange(false);
    } catch (e) {
      const err = e as { response?: { data?: { conflicts?: Array<{ kind: string; currentTrip: string }> } } };
      if (err.response?.data?.conflicts) {
        setConflicts(err.response.data.conflicts);
      } else {
        window.alert(extractErrorMessage(e));
      }
    } finally { setSubmitting(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Reassign vehicle / driver</SheetTitle>
          <SheetDescription>Admin override. Audit trail captures the change.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
          <div className="space-y-1">
            <Label>Vehicle</Label>
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
              <option value="">— keep current —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.code} · {v.plate_en} · {v.availability}{v.current_trip_code ? ` (${v.current_trip_code})` : ''}
                </option>
              ))}
            </select>
            {vehicles.find((v) => v.id === vehicleId) && (
              <p className={'text-[11px] ' + tone(vehicles.find((v) => v.id === vehicleId)!.availability)}>
                Status: {vehicles.find((v) => v.id === vehicleId)!.availability}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Driver</Label>
            <DriverSelect
              value={driverId}
              onChange={setDriverId}
              drivers={drivers.map(toDriverOption)}
              noneLabel="— keep current —"
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
              // Availability is why somebody opens this sheet, so it stays.
              renderLabel={(d) => [
                d.code, d.name, d.iqama, d.availability,
              ].filter(Boolean).join(' · ') + (d.current_trip_code ? ` (${d.current_trip_code})` : '')}
            />
            {drivers.find((d) => d.id === driverId) && (
              <p className={'text-[11px] ' + tone(drivers.find((d) => d.id === driverId)!.availability)}>
                Status: {drivers.find((d) => d.id === driverId)!.availability}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Reason *</Label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base"
              placeholder="e.g. truck went into maintenance, swapping to spare" />
          </div>
          {conflicts.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
              <div className="font-medium text-amber-700 dark:text-amber-400 mb-1">Conflicts found:</div>
              <ul className="list-disc pl-5">
                {conflicts.map((c, i) => <li key={i}>{c.kind} is on {c.currentTrip}</li>)}
              </ul>
              <label className="inline-flex items-center gap-1.5 mt-2">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="h-4 w-4" />
                <span>Override and reassign anyway</span>
              </label>
            </div>
          )}
        </div>
        <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !reason}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Reassign
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
